import { waivedShipping, } from '@cod/config-schema';
import { box, button, column, field, image, row, styled, text } from '@tiendanube/nube-sdk-ui';
import { computeTotal, formatMoney } from './format.js';
import { orderLineItems, singleLineStepper as isSingleLineStepper } from './lines.js';
import { applyTierDiscount, cartOfferDiscount, offerVariantSource, quantityDiscountExclusion, tierBase, tierById, unitVariantIds, } from './offers.logic.js';
import { discountApplyCss, discountWrapCss } from './theme.js';
import { DISCOUNT_APPLIED_PREFIX, DISCOUNT_REMOVE_TEXT } from './strings.js';
/**
 * Interpolate the merchant-authored text tokens.
 *
 * Three tokens, and the legacy used a different subset in each place (`interpolateBlockText`,
 * `interpolateWaText`, `submitText`). Handling all three everywhere is strictly more permissive and
 * cannot break a config that only used one — an unknown token is left untouched either way.
 *
 *  - `{order_total}`    — what the buyer pays, **including shipping**. The first port used the
 *                         merchandise total, so a form with a paid rate showed one figure on the
 *                         submit button and another in the summary.
 *  - `{order_subtotal}` — merchandise minus discount, before shipping (the legacy `lineTotal()`).
 *  - `{product_name}`   — the page product.
 */
export function tokens(o, s) {
    return s
        .replace(/\{order_total\}/g, money(o, orderTotal(o)))
        .replace(/\{order_subtotal\}/g, money(o, Math.max(0, lineTotal(o))))
        .replace(/\{product_name\}/g, o.ctx.product.name);
}
/** Money in the store's currency + locale, exactly as the legacy `money()` helper did. */
function money(o, amount) {
    return formatMoney(amount, o.ctx.currency, o.ctx.locale ?? 'es');
}
/**
 * Cart lines the buyer has NOT removed with the per-item ✕.
 *
 * The legacy kept a mutable `activeLineItems` array; state is caller-owned here, so the removals
 * live in `FormState.removedVariantIds` and every reader filters through this. Anything that reads
 * `ctx.lineItems` directly would price and submit a line the buyer already dropped.
 */
export function activeLines(o) {
    return orderLineItems(o.config, o.ctx, o.build.state, o.build.offer);
}
/**
 * Whether a one-line cart form shows the quantity stepper.
 *
 * Only for a SINGLE line, and only when the merchant has not hidden the controls. A tier owns the
 * quantity while it is selected, so the stepper stands down for it too.
 */
export function singleLineStepper(o) {
    return isSingleLineStepper(o.config, o.ctx, o.build.state, o.build.offer);
}
/** The quantity-offer tier the buyer has selected, when an offer is on screen. */
export function activeTier(o) {
    return o.build.offer ? tierById(o.build.offer, o.build.state.selectedTierId) : undefined;
}
/**
 * The tier's per-unit variant ids, resolved. `state.tierVariantIds` is sparse — only the units the
 * buyer picked — so it must never reach `tierBase` directly: the gaps are what let the untouched
 * units follow the buyer's product-page variant, and a raw read prices them as `undefined`.
 */
function unitIds(o, tier) {
    return unitVariantIds(tier, o.ctx, o.build.state.tierVariantIds);
}
/**
 * Merchandise total before shipping and discounts.
 *
 * With a tier active this is its **variant-summed** base — the sum of what the buyer picked in the
 * per-unit dropdowns — because a tier whose units are different variants costs what those variants
 * cost, not `page variant × quantity` (`form.ts:378-381`).
 */
export function baseTotal(o) {
    const lines = o.ctx.lineItems ? activeLines(o) : undefined;
    if (lines)
        return computeTotal({ ...o.ctx, quantity: o.build.state.quantity, lineItems: lines });
    const tier = activeTier(o);
    if (tier)
        return tierBase(tier, o.ctx, unitIds(o, tier));
    return computeTotal({ ...o.ctx, quantity: o.build.state.quantity });
}
/**
 * The pre-discount total a tier's discount applies to.
 *
 * In cart mode that is ONLY the offer's own lines: the rest of the cart is not part of the offer,
 * so discounting the whole subtotal would hand out the tier's saving on unrelated products
 * (`form.ts:387-395`).
 */
function offerBase(o, tier) {
    const lines = o.ctx.lineItems ? activeLines(o) : null;
    if (!lines)
        return tierBase(tier, o.ctx, unitIds(o, tier));
    const pageProductId = o.ctx.pageProductId;
    if (pageProductId == null)
        return lines.reduce((sum, li) => sum + li.price * li.quantity, 0);
    return lines
        .filter((li) => li.id === pageProductId)
        .reduce((sum, li) => sum + li.price * li.quantity, 0);
}
/**
 * The saving the selected tier grants.
 *
 * Surfaced on the same "Descuento" line as a coupon, so the buyer sees it the moment they pick a
 * tier rather than only in the total.
 */
export function offerDiscountAmount(o) {
    const tier = activeTier(o);
    const base = tier ? offerBase(o, tier) : 0;
    const picked = tier ? Math.max(0, base - applyTierDiscount(base, tier)) : 0;
    return picked + cartQuantityDiscount(o);
}
/**
 * The saving the cart earns from its quantity with no tier picked — the drawer's own discount.
 *
 * 🚨 Cart mode only, and it is not an extra feature: the Discount API discounts Tiendanube's cart
 * from its contents on every change, so a form opened from the drawer that priced only selected
 * tiers quoted MORE than the drawer behind it (€30,00 against €27,00 for the same two units) and
 * created the order at the higher number. `cartOfferDiscount` is the same rule the callback runs.
 *
 * On the product page there are no cart lines and the buyer picks a tier explicitly, so nothing
 * here applies — an untouched product-page form still discounts only what was chosen.
 */
function cartQuantityDiscount(o) {
    if (!o.ctx.lineItems)
        return 0;
    const offers = o.config._future.quantity_offers;
    // Only the offer whose tier this form has ALREADY priced is skipped — see
    // `quantityDiscountExclusion`. Passing `o.build.offer` unconditionally, as this used to, skipped
    // the page's offer even when the buyer had picked no tier at all, so a cart that plainly earned
    // a tier by quantity showed no "Descuento" row while the order was still discounted.
    return cartOfferDiscount(offers, activeLines(o), quantityDiscountExclusion(offers, activeTier(o), o.build.offer));
}
/** The line total after the offer's discount but before any coupon — the legacy `lineTotal()`. */
export function lineTotal(o) {
    return baseTotal(o) - offerDiscountAmount(o);
}
/** The discount the applied coupon quote grants, or 0. */
export function couponAmount(o) {
    return o.build.state.discount?.ok ? (o.build.state.discount.discount ?? 0) : 0;
}
/** Everything on the "Descuento" line: the quantity offer's saving plus any applied coupon. */
export function discountAmount(o) {
    return offerDiscountAmount(o) + couponAmount(o);
}
/** The rate the buyer picked, if it is still one the merchant has configured. */
function selectedRate(o) {
    return o.config.shipping_rates.find((r) => r.id === o.build.state.selectedRateId);
}
/** The rates on offer for this cart — what `shippingNode` draws as cards. */
function offeredRates(o) {
    const ids = o.build.eligibleRateIds;
    return ids ? o.config.shipping_rates.filter((r) => ids.includes(r.id)) : o.config.shipping_rates;
}
/** The selected rate's configured price, before any free-shipping coupon. */
export function shippingCost(o) {
    return selectedRate(o)?.price ?? 0;
}
/**
 * The part of that price a free-shipping coupon waives.
 *
 * Computed live against the CURRENT selection rather than read off the quote, because the buyer
 * can switch rate after applying the code — see `DiscountQuote.free_shipping`. `waivedShipping`
 * is shared with the backend, so the row below and the created order agree by construction.
 */
export function shippingDiscount(o) {
    const quote = o.build.state.discount;
    if (!quote?.ok || !quote.free_shipping)
        return 0;
    return waivedShipping(selectedRate(o), offeredRates(o), {
        freeShipping: true,
        onlyCheapest: quote.only_cheapest_shipping === true,
    });
}
/** What the buyer actually pays for shipping. */
export function shippingCharged(o) {
    return Math.max(0, shippingCost(o) - shippingDiscount(o));
}
/**
 * What the buyer pays: merchandise − discount + shipping, never below zero.
 *
 * 🚨 The shipping waiver is subtracted INSIDE `shippingCharged`, never added to `discountAmount`.
 * Merchandise is clamped at zero, so a coupon big enough to reach that clamp would have swallowed
 * the free shipping too and quoted a total that still charged for delivery.
 */
export function orderTotal(o) {
    return Math.max(0, baseTotal(o) - discountAmount(o)) + shippingCharged(o);
}
/**
 * The product card's price line. Mirrors the offer card when a tier is active: struck-through base
 * → discounted net, so nothing on screen disagrees about what the bundle costs.
 */
function productPriceNodes(o) {
    const s = o.styles;
    if (!activeTier(o)) {
        return [text({ style: s.productCard.price, children: [money(o, o.ctx.variant.price)] })];
    }
    const base = baseTotal(o);
    const net = lineTotal(o);
    return [
        row({
            style: { ...s.productCard.price, display: 'flex', alignItems: 'baseline', gap: '6px' },
            children: [
                ...(net < base
                    ? [text({ key: 'compare', style: s.productCompare, children: [money(o, base)] })]
                    : []),
                text({ key: 'net', style: s.productNet, children: [money(o, net)] }),
            ],
        }),
    ];
}
/**
 * The per-unit variants an active tier sells, as chips under the price. Ports `variantSummary` /
 * `setVariantSummary` (`form-renderer/src/form.ts:475-486`).
 *
 * 🚨 Only for offers rendered ABOVE THE BUY BUTTON. Those cards live outside the form, so once the
 * modal opens the buyer has no other way to see what they chose — the card's own variant line is
 * the PAGE variant and says "Blue / XS" beside a bundle of Beige and Yellow. An in-form offer
 * needs none of this: its pickers are on screen a few blocks below.
 *
 * They vanish on their own when the buyer touches the stepper, because that clears the tier.
 */
function variantChipNodes(o) {
    const offer = o.build.offer;
    const tier = activeTier(o);
    if (!offer?.design.display_above_buy_button || !tier)
        return [];
    const { variants } = offerVariantSource(offer, o.ctx);
    const labelById = new Map(variants.map((v) => [v.id, v.name]));
    const labels = unitIds(o, tier)
        .map((id) => labelById.get(id))
        .filter((label) => Boolean(label));
    if (!labels.length)
        return [];
    return [
        row({
            key: 'variant-chips',
            style: o.styles.productVariants,
            children: labels.map((label, i) => text({ key: `chip-${i}`, style: o.styles.productVariant, children: [label] })),
        }),
    ];
}
/* ── Order summary ─────────────────────────────────────────────────────────────────────── */
/**
 * The product card: thumbnail with a quantity badge, name, variant label, price, and the stepper.
 *
 * In cart mode the same block lists every cart line instead (`ctx.lineItems`), which is why the
 * legacy called it `productCardNode` in one branch and `cartList` in the other.
 */
export function orderSummaryNode(o) {
    const s = o.styles;
    const lines = o.ctx.lineItems;
    if (lines?.length) {
        const active = activeLines(o);
        const remove = o.build.handlers?.onRemoveLineItem;
        // `const removable = activeLineItems.length > 1` — an order needs at least one line, so the
        // last item cannot be removed and its ✕ disappears rather than sitting there inert.
        const removable = active.length > 1 && Boolean(remove);
        const withStepper = singleLineStepper(o);
        const rows = active.map((li, i) => row({
            key: `line-${li.variantId ?? i}`,
            // `.cod-cart-item:last-child { border-bottom: none }` — the separator between rows is
            // the whole visual structure of this block, and the first port had none at all.
            style: i === active.length - 1 ? s.cartList.itemLast : s.cartList.item,
            children: [
                li.image
                    ? image({ src: li.image, alt: li.name, style: s.cartList.thumb })
                    : box({ style: s.cartList.thumbEmpty, children: [] }),
                column({
                    key: 'text',
                    style: s.cartList.text,
                    children: [
                        // `${li.name} × ${li.quantity}` — the quantity rides on the NAME line, and the
                        // price column shows the line total. The first port put "3 × 10.00" in the price
                        // column and no quantity on the name.
                        text({ style: s.cartList.name, children: [`${li.name} × ${li.quantity}`] }),
                        ...(li.variant ? [text({ style: s.cartList.variant, children: [li.variant] })] : []),
                        // 🚨 INSIDE the text column, under the name — the same place the product card puts
                        // it, under the price in its info column. Rendered below the whole list instead, it
                        // sat at the container's left edge beneath the thumbnail rather than lining up with
                        // the row it belongs to.
                        ...(withStepper
                            ? [stepperNode(o, Math.max(1, o.build.state.quantity), s.cartList.stepper)]
                            : []),
                    ],
                }),
                text({
                    key: 'price',
                    style: s.cartList.price,
                    children: [money(o, li.price * li.quantity)],
                }),
                ...(removable
                    ? [
                        button({
                            key: 'remove',
                            style: s.cartList.remove,
                            children: ['×'],
                            onClick: () => remove?.(li.variantId ?? 0),
                        }),
                    ]
                    : []),
            ],
        }));
        return column({ style: s.cartList.container, children: rows });
    }
    const qty = o.build.state.quantity;
    const chips = variantChipNodes(o);
    return box({
        style: s.productCard.container,
        children: [
            ...(o.ctx.product.image
                ? [
                    // A relative wrapper so the badge can overlap the corner (`.cod-thumb-wrap`).
                    box({
                        style: s.productCard.thumbWrap,
                        children: [
                            image({
                                src: o.ctx.product.image,
                                alt: o.ctx.product.name,
                                style: s.productCard.thumb,
                            }),
                            text({ style: s.productCard.badge, children: [String(qty)] }),
                        ],
                    }),
                ]
                : []),
            column({
                // `.cod-product-info { flex: 1; min-width: 0 }` — and NO gap. The spacing between the
                // three lines is their own margins (see `productCard.variant` / `.price`); a gap on top of
                // them is spacing the Designer never shows.
                style: { flexGrow: '1', minWidth: '0' },
                children: [
                    text({ style: s.productCard.name, children: [o.ctx.product.name] }),
                    // The page variant — but ONLY when the chips are not listing what the tier actually
                    // sells. Showing "Blue / XS" above chips reading "Yellow / S" and "Beige / M" states
                    // two different things about the same order.
                    ...(o.ctx.variant.name && !chips.length
                        ? [text({ style: s.productCard.variant, children: [o.ctx.variant.name] })]
                        : []),
                    // Without a tier: the UNIT price (`priceEl = el(…, [money(unitPrice)])`) — the line
                    // total belongs to the summary box and the quantity is already on the badge.
                    // With one: the tier's line total, struck-through base → discounted net, so the card,
                    // the offer card and the summary all agree (`renderProductPrice`, `form.ts:423`).
                    ...productPriceNodes(o),
                    // The variants an above-the-button tier sells, which the buyer cannot otherwise see
                    // once the modal is open.
                    ...chips,
                    // The stepper sits UNDER the price, matching the Designer. Placing it beside the
                    // text column pushed it to the card's right edge.
                    // `hide_quantity_controls` is forced on in cart mode by the caller, so quantities
                    // are edited in the cart rather than in two places that can disagree.
                    ...(o.config.product_card.hide_quantity_controls ? [] : [stepperNode(o, qty)]),
                ],
            }),
        ],
    });
}
/**
 * The −/+ quantity stepper.
 *
 * Built from buttons rather than `numberfield`: the design is a bordered group with the count
 * between two square buttons, and `numberfield`'s own increment/decrement are laid out by the host.
 * Buttons keep the frame under `product_card`'s `qc_*` styling, which the merchant configures.
 */
function stepperNode(o, qty, extra) {
    const s = o.styles.numberField;
    const change = o.build.handlers?.onQuantityChange;
    const step = (next) => change ? { onClick: () => change(Math.max(1, next)) } : {};
    // `box`, not `row`: `row` stretches to its parent's cross axis, which made the group span the
    // whole product card with `−` and `+` pinned to opposite edges. `wrapper` carries the
    // `inline-flex` + `fit-content` sizing from `.cod-stepper`.
    return box({
        style: { ...s.wrapper, ...(extra ?? {}) },
        // Keys, because `−` and `+` differ ONLY in their (array) children and a closure written at the
        // same source position — neither reaches the SDK's props hash, so the two buttons collided on
        // one id and the stepper went dead. See `identity.ts`.
        children: [
            button({ key: 'minus', children: ['−'], style: s.decrementButton, ...step(qty - 1) }),
            text({ key: 'value', style: s.input, children: [String(qty)] }),
            button({ key: 'plus', children: ['+'], style: s.incrementButton, ...step(qty + 1) }),
        ],
    });
}
/* ── Discount code ─────────────────────────────────────────────────────────────────────── */
export function discountNode(o) {
    const d = o.config.discount;
    const quote = o.build.state.discount;
    const apply = o.build.handlers?.onApplyDiscount;
    const remove = o.build.handlers?.onRemoveDiscount;
    const onCode = o.build.handlers?.onDiscountCodeChange;
    /** A code is ON when the backend answered with an applicable quote — the same test the
     *  confirmation line below uses, so the button and the message can never disagree. */
    const applied = quote?.ok === true;
    // Same overlay as every other input: the host ignores `field`'s `placeholder` prop, so the
    // Designer's discount placeholder (and its own colour setting) only appears if we draw it.
    // `discountWrapCss` turns it transparent on focus, so typing never needs a rebuild.
    const StyledWrap = styled(box) `
    ${discountWrapCss(o.config)}
  `;
    // The Apply button's geometry goes through `styled()`, not the style prop: three rounds of
    // inline padding / min-height / height left it half again as tall as the input, because a
    // theme's own button sizing outranks a plain inline declaration.
    const StyledApply = styled(button) `
    ${discountApplyCss(o.config)}
  `;
    return column({
        style: o.styles.discount.container,
        children: [
            row({
                key: 'row',
                // `.cod-discount { display: flex; gap: 8px }` — `align-items` is left at its `stretch`
                // default, which is what makes the Apply button exactly as tall as the input.
                style: { display: 'flex', gap: '8px', alignItems: 'stretch', width: '100%' },
                children: [
                    // `position: relative` so the placeholder overlay anchors to the input, and
                    // `flex: 1` so the input takes the leftover width (`.cod-discount .cod-input`).
                    StyledWrap({
                        key: 'input',
                        style: o.styles.discount.inputWrap,
                        children: [
                            field({
                                name: '__discount_code',
                                label: d.field_label,
                                // Uncontrolled, like every other input, and no native placeholder — the visible
                                // one is the overlay below, and two would double up.
                                placeholder: '',
                                // NO `display: flex` on the container. The ordinary fields fill their wrapper with
                                // `width: 100%` alone, and adding a flex context here is the one difference that
                                // left the discount input at a stub of the row's width on a live store.
                                style: {
                                    // `nativeContainer` for the theme's `form-group` margin, which otherwise pushed
                                    // everything under the discount row down by 20px. See its header in `theme.ts`.
                                    container: o.styles.nativeContainer,
                                    label: o.styles.nativeLabelHidden,
                                    input: o.styles.discount.input,
                                },
                                ...(onCode
                                    ? {
                                        onChange: (e) => onCode(String(e.value ?? '')),
                                    }
                                    : {}),
                            }),
                            // The overlay's colour comes from `discountWrapCss` on the wrapper above — the
                            // discount input has its OWN placeholder colour setting, and `styles.placeholder`
                            // is `color: inherit` precisely so the wrapper can both colour it and blank it on
                            // focus. Spreading an explicit colour here would re-break the focus behaviour.
                            ...(o.build.state.discountCode
                                ? []
                                : [text({ style: o.styles.placeholder, children: [d.field_label] })]),
                        ],
                    }),
                    StyledApply({
                        // 🚨 The key CHANGES with the state, which is what guarantees the two buttons are
                        // different nodes. `withIdentity` derives ids from ancestor keys, and the SDK's own
                        // hash drops array children — so an apply-vs-remove button that differed only in its
                        // caption could otherwise land on one id and keep painting the old label. See
                        // `identity.ts`.
                        key: applied ? 'remove' : 'apply',
                        // 🚨 A BARE STRING, not a `text()` node. `text()` renders paragraph-like, and a theme's
                        // `p { margin: 1em 0 }` inflates the button by roughly 30px. The stepper's `−`/`+`
                        // buttons have always passed bare strings for the same reason.
                        children: [applied ? DISCOUNT_REMOVE_TEXT : d.apply_text],
                        style: o.styles.discount.applyButton,
                        ...(applied
                            ? remove
                                ? { onClick: () => remove() }
                                : {}
                            : apply
                                ? { onClick: () => apply() }
                                : {}),
                    }),
                ],
            }),
            // Feedback replaces the legacy hidden `<div>`: shown only once there is a quote to report.
            //
            // Two things the first port got wrong, both from `showDiscountFeedback` (`form.ts:1123`):
            //
            //  - success said `line_text` ("Descuento") — the label for the totals ROW, not a
            //    confirmation. The legacy says `Cupon aplicado: -<amount>`, so the buyer sees what the
            //    code was actually worth.
            //  - failure showed `quote.error`, the backend's internal reason. The legacy deliberately
            //    shows the merchant's configured `invalid_error` instead ("not the backend's internal
            //    reason string" — its own comment).
            ...(quote
                ? [
                    text({
                        style: quote.ok ? o.styles.discount.appliedLine : o.styles.discount.errorLine,
                        children: [
                            quote.ok
                                ? // Not `quote.discount`: a free-shipping coupon quotes 0 there and is worth
                                    // the waived rate instead, so the buyer would have read "-0,00" for a code
                                    // that had just taken the delivery charge off their total.
                                    `${DISCOUNT_APPLIED_PREFIX}: -${money(o, (quote.discount ?? 0) + shippingDiscount(o))}`
                                : d.invalid_error,
                        ],
                    }),
                ]
                : []),
        ],
    });
}
/* ── Totals ────────────────────────────────────────────────────────────────────────────── */
function totalsRow(o, key, label, value, opts) {
    return row({
        // An explicit key so the row keeps its identity when a discount line appears above or below
        // it — see `identity.ts`. Without one, the rows are told apart only by position.
        key,
        style: {
            justifyContent: 'space-between',
            width: '100%',
            ...(opts?.row ?? o.styles.totals.row),
        },
        children: [
            text({ style: o.styles.text, children: [label] }),
            text({ style: opts?.value ?? o.styles.text, children: [value] }),
        ],
    });
}
export function totalsNode(o) {
    const t = o.config.totals_style;
    const discount = discountAmount(o);
    // The shipping row appears whenever rates are offered — shipping is chosen in its own block.
    const hasRates = o.config.shipping_rates.length > 0;
    // The CHARGED cost, so a rate waived by a free-shipping coupon falls through to the merchant's
    // `shipping_label` ("Gratis") exactly like a rate the merchant priced at zero.
    const cost = shippingCharged(o);
    // Flat: the rows are direct children of the box. The first port nested a bare `column` in
    // between, which bought nothing and gave the tree an extra unkeyed node.
    return column({
        style: o.styles.totals.container,
        children: [
            totalsRow(o, 'subtotal', t.subtotal_title, money(o, baseTotal(o))),
            ...(hasRates
                ? [
                    totalsRow(o, 'shipping', t.shipping_title, 
                    // `shippingLabelText()` — the selected rate's price, or the free-shipping label at 0.
                    cost > 0 ? money(o, cost) : o.config.summary.shipping_label),
                ]
                : []),
            ...(discount > 0
                ? [totalsRow(o, 'discount', o.config.discount.line_text, `-${money(o, discount)}`)]
                : []),
            box({ key: 'divider', style: o.styles.totals.divider, children: [] }),
            totalsRow(o, 'total', t.total_title, money(o, orderTotal(o)), {
                row: o.styles.totals.totalRow,
                value: o.styles.totals.totalAmount,
            }),
            ...(t.show_taxes && t.taxes_message
                ? [text({ key: 'taxes', style: o.styles.taxesMessage, children: [t.taxes_message] })]
                : []),
        ],
    });
}
/* ── Shipping method ───────────────────────────────────────────────────────────────────── */
/**
 * Selectable rate cards, or the legacy single static option when no rates are configured.
 *
 * Rate CONDITIONS (country / order total / quantity gating) are evaluated by
 * `config-schema/shipping.logic`, which the caller applies before passing `eligibleRateIds`. Doing
 * it here would need the cart facts the builder deliberately does not have.
 */
export function shippingNode(o, eligibleRateIds) {
    const sm = o.config.shipping_method;
    const select = o.build.handlers?.onRateSelect;
    const configured = o.config.shipping_rates;
    const rates = configured.filter((r) => !eligibleRateIds || eligibleRateIds.includes(r.id));
    // `shippingNode.style.display = eligible.length ? '' : 'none'` — with rates configured but none
    // eligible for this cart, the whole section disappears. The static fallback card below is for
    // the *other* case, where the merchant configured no rates at all. Conflating the two showed a
    // placeholder card to buyers whose cart had simply filtered every rate out.
    if (configured.length > 0 && rates.length === 0)
        return null;
    const options = rates.length
        ? rates.map((r) => {
            const selected = o.build.state.selectedRateId === r.id;
            return button({
                // 🚨 The rate id as an explicit key. Two cards differ only in their children and their
                // click closure — neither of which reaches the SDK's props hash — so without this they
                // collide on one `__internalId` and BOTH clicks reach the same handler. See
                // `identity.ts`; this is what "I can't click free shipping again" was.
                key: `rate-${r.id}`,
                style: selected ? o.styles.shippingMethod.optionSelected : o.styles.shippingMethod.option,
                ...(select ? { onClick: () => select(r.id) } : {}),
                // TWO children, exactly like `.cod-shipping-card`: the radio-plus-text group, then the
                // price. The card's own `justify-content: space-between` pushes them apart. Wrapping
                // both in one extra row (the first port) made the button's space-between a no-op and let
                // the price drift towards the middle.
                children: [
                    row({
                        key: 'option',
                        // `.cod-shipping-option { display: flex; align-items: center; gap: 12px }`
                        style: { display: 'flex', alignItems: 'center', gap: '12px', minWidth: '0' },
                        children: [
                            // A round radio marker, not a checkbox. NubeSDK has no radio component, and a
                            // square box beside the Designer's round one is the first thing a merchant sees.
                            // `.cod-radio` + `.cod-radio-on::after` — a bordered circle with an inner dot.
                            box({
                                key: 'radio',
                                style: selected ? o.styles.shippingMethod.radioOn : o.styles.shippingMethod.radio,
                                children: selected
                                    ? [box({ style: o.styles.shippingMethod.radioDot, children: [] })]
                                    : [],
                            }),
                            // `.cod-shipping-text { display: flex; flex-direction: column }` — the rate's
                            // name sits above its optional description, which the first port never rendered.
                            column({
                                key: 'text',
                                style: { minWidth: '0' },
                                children: [
                                    text({ style: o.styles.shippingMethod.optionName, children: [r.name] }),
                                    ...(r.description
                                        ? [
                                            text({
                                                style: o.styles.shippingMethod.optionDesc,
                                                children: [r.description],
                                            }),
                                        ]
                                        : []),
                                ],
                            }),
                        ],
                    }),
                    text({
                        key: 'price',
                        style: o.styles.shippingMethod.optionPrice,
                        children: [r.price > 0 ? money(o, r.price) : o.config.summary.shipping_label],
                    }),
                ],
            });
        })
        : [
            // No rates configured: the legacy static card, label and price straight from config. It
            // also carries the "on" radio, which the first port dropped — the Designer shows a
            // selected option here, because there is nothing else to pick.
            row({
                key: 'static',
                style: o.styles.shippingMethod.option,
                children: [
                    row({
                        key: 'option',
                        style: { display: 'flex', alignItems: 'center', gap: '12px', minWidth: '0' },
                        children: [
                            box({
                                style: o.styles.shippingMethod.radioOn,
                                children: [box({ style: o.styles.shippingMethod.radioDot, children: [] })],
                            }),
                            text({ style: o.styles.shippingMethod.optionName, children: [sm.option_label] }),
                        ],
                    }),
                    text({
                        key: 'price',
                        style: o.styles.shippingMethod.optionPrice,
                        children: [sm.price_label],
                    }),
                ],
            }),
        ];
    return column({
        style: o.styles.shippingMethod.container,
        children: [
            text({ style: o.styles.shippingMethod.title, children: [sm.title] }),
            column({ style: o.styles.shippingMethod.options, children: options }),
        ],
    });
}
/* ── Inline custom blocks ──────────────────────────────────────────────────────────────── */
/** "Title or text": free text with its own typography, supporting `{order_total}`. */
export function customTextNode(f, o) {
    const tb = f.text_block;
    if (!tb)
        return [];
    const content = tokens(o, tb.content);
    return [
        text({
            // `.cod-custom-text` supplies the layout; the merchant's typography overrides it.
            style: {
                ...o.styles.customText,
                textAlign: tb.align,
                fontSize: `${tb.font_size}px`,
                fontWeight: tb.font_weight,
                color: tb.text_color,
            },
            children: [content],
        }),
    ];
}
/** "Image or GIF": rendered at a percentage of the form width. */
export function customImageNode(f, o) {
    const im = f.image;
    if (!im?.url)
        return [];
    return [
        box({
            style: o.styles.customImageWrap,
            children: [
                image({
                    src: im.url,
                    alt: '',
                    style: { ...o.styles.customImage, width: `${im.image_size}%` },
                }),
            ],
        }),
    ];
}
//# sourceMappingURL=blocks.js.map